import { useReducer, useState } from 'react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import {
  DATE_PERIODS,
  CONTENT_REPORT_STATUS,
  SUPPORT_TICKET_STATUS,
} from '../constants/supportTicket'
import { SUBSCRIPTION_STATUS_OPTIONS, SUBSCRIPTION_TIERS } from '../constants/subscriptionPlans'
import { FILTER_TYPES, SORT_MODES } from '../constants/filters'
import SelectField from '../components/common/SelectField'
import CustomPeriodFilter from '../components/common/CustomPeriodFilter'
import { CONTENT_TYPES } from '../constants/contentTypes'
import USER_TYPE from '../constants/userType'
import Pagination from '../components/common/Pagination'
import { VERIFICATION_STATUSES } from '../constants/verificationStatuses'
import useProductCategories from './useProductCategories'

export function useFilters(initial = {}, placeholders = {}) {
  const [tempSearch, setTempSearch] = useState('')
  const [showCustomPeriod, setShowCustomPeriod] = useState(false)
  const [filters, updateFilters] = useReducer((state, action) => ({ ...state, ...action }), {
    page: 1,
    sortMode: SORT_MODES.LATEST.key,
    q: '',
    userType: '',
    isActive: '',
    isApproved: '',
    period: '',
    subscriptionTier: '',
    categorySlug: '',
    ...initial,
  })
  const { categories } = useProductCategories()

  const renderSearchInput = (key) => (
    <div className="relative flex-1" key={key}>
      <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input
        type="text"
        placeholder={placeholders.search || 'Search by name or phone number'}
        value={tempSearch}
        onChange={(e) => setTempSearch(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            updateFilters({ q: tempSearch })
          }
        }}
        className="w-full h-10 border border-gray-200 rounded-md pl-10 pr-3 py-2 bg-secondary-50 text-sm text-foreground placeholder:text-gray-400 focus:outline-none focus:border-secondary-600 focus:ring-1 focus:ring-secondary-600/20 transition-colors"
      />
    </div>
  )

  const renderUserTypeFilter = (key) => (
    <SelectField
      value={filters.userType}
      onChange={(e) => updateFilters({ userType: e.target.value })}
      key={key}
      className="capitalize"
    >
      <option value="">All User Types</option>
      {[USER_TYPE.customer, USER_TYPE.retailer, USER_TYPE.wholeseller].map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </SelectField>
  )

  const renderIsActiveFilter = (key) => (
    <SelectField
      value={filters.isActive}
      onChange={(e) => updateFilters({ isActive: e.target.value })}
      key={key}
    >
      {SUBSCRIPTION_STATUS_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </SelectField>
  )

  const renderIsApprovedFilter = (key) => (
    <SelectField
      value={filters.isApproved}
      onChange={(e) => updateFilters({ isApproved: e.target.value })}
      key={key}
    >
      <option value="">All Verification</option>
      <option value="1">Verified</option>
      <option value="0">Not Verified</option>
    </SelectField>
  )

  const onDateSelectChange = (e) => {
    const selectedValue = e.target.value

    if (selectedValue === DATE_PERIODS.custom.toLowerCase() || selectedValue === filters.period) {
      setShowCustomPeriod(true)
      return
    }

    updateFilters({ period: selectedValue })
  }

  const getOptionValue = (opt) => {
    if (opt !== DATE_PERIODS.custom.toLowerCase()) return opt

    return filters.period || DATE_PERIODS.custom.toLowerCase()
  }

  const renderDateFilter = (key) => (
    <div className="flex flex-1 gap-2 items-center" key={key}>
      <SelectField value={filters.period} onChange={onDateSelectChange} key={key}>
        <option value="">Date</option>
        {Object.keys(DATE_PERIODS).map((opt) => (
          <option key={opt} value={getOptionValue(opt)}>
            {DATE_PERIODS[opt]}
          </option>
        ))}
      </SelectField>
      {showCustomPeriod && (
        <CustomPeriodFilter
          handleFilterPeriod={(period) => {
            setShowCustomPeriod(false)
            if (!period) {
              updateFilters({ period: '' })
              return
            }

            updateFilters({ period })
          }}
          open={showCustomPeriod}
          period={filters.period}
        />
      )}
    </div>
  )

  const renderSubscriptionTierFilter = (key) => (
    <SelectField
      value={filters.subscriptionTier}
      onChange={(e) => updateFilters({ subscriptionTier: e.target.value })}
      key={key}
    >
      <option value="">All Subscription Tiers</option>
      {Object.values(SUBSCRIPTION_TIERS).map((tier) => (
        <option key={tier} value={tier}>
          {tier}
        </option>
      ))}
    </SelectField>
  )

  const renderContentTypeFilter = (key) => (
    <SelectField
      value={filters.contentType}
      onChange={(e) => updateFilters({ contentType: e.target.value })}
      key={key}
      className="capitalize"
    >
      <option value="">All Content Types</option>
      {Object.values(CONTENT_TYPES).map((type) => (
        <option key={type} value={type} className="capitalize">
          {type.split('_').join(' ')}
        </option>
      ))}
    </SelectField>
  )

  const renderReportTicketStatusFilter = (key) => (
    <SelectField
      value={filters.status}
      onChange={(e) => updateFilters({ status: e.target.value })}
      key={key}
    >
      <option value="">All Ticket Statuses</option>
      {Object.values(CONTENT_REPORT_STATUS).map((status) => (
        <option key={status.key} value={status.key}>
          {status.label}
        </option>
      ))}
    </SelectField>
  )

  const renderSupportTicketStatusFilter = (key) => (
    <SelectField
      value={filters.status}
      onChange={(e) => updateFilters({ status: e.target.value })}
      key={key}
    >
      <option value="">All Ticket Statuses</option>
      {Object.values(SUPPORT_TICKET_STATUS).map((status) => (
        <option key={status.key} value={status.key}>
          {status.label}
        </option>
      ))}
    </SelectField>
  )

  const renderSortFilter = (key) => (
    <SelectField
      value={filters.sortMode}
      onChange={(e) => updateFilters({ sortMode: e.target.value })}
      key={key}
    >
      {Object.values(SORT_MODES).map((mode) => (
        <option key={mode.key} value={mode.key}>
          {mode.label}
        </option>
      ))}
    </SelectField>
  )

  const renderShopVerificationStatusFilter = (key) => (
    <SelectField
      value={filters.status}
      onChange={(e) => updateFilters({ status: e.target.value })}
      key={key}
    >
      <option value="">All Verification Statuses</option>
      {Object.entries(VERIFICATION_STATUSES).map(([statusKey, statusObj]) => (
        <option key={statusKey} value={statusObj.value}>
          {statusObj.label}
        </option>
      ))}
    </SelectField>
  )

  const renderCategoryFilter = (key) => (
    <SelectField
      value={filters.categorySlug}
      onChange={(e) => updateFilters({ categorySlug: e.target.value })}
      key={key}
    >
      <option value="">All Categories</option>
      {categories.map((category) => (
        <option key={category.categorySlug} value={category.categorySlug}>
          {category.name}
        </option>
      ))}
    </SelectField>
  )

  const renderFilters = (filterTypes) => {
    return filterTypes.map((filterType) => {
      switch (filterType) {
        case FILTER_TYPES.search:
          return renderSearchInput(filterType)
        case FILTER_TYPES.userType:
          return renderUserTypeFilter(filterType)
        case FILTER_TYPES.isActive:
          return renderIsActiveFilter(filterType)
        case FILTER_TYPES.date:
          return renderDateFilter(filterType)
        case FILTER_TYPES.subscriptionTier:
          return renderSubscriptionTierFilter(filterType)
        case FILTER_TYPES.contentType:
          return renderContentTypeFilter(filterType)
        case FILTER_TYPES.supportTicketStatus:
          return renderSupportTicketStatusFilter(filterType)
        case FILTER_TYPES.reportTicketStatus:
          return renderReportTicketStatusFilter(filterType)
        case FILTER_TYPES.sort:
          return renderSortFilter(filterType)
        case FILTER_TYPES.isApproved:
          return renderIsApprovedFilter(filterType)
        case FILTER_TYPES.shopVerificationStatus:
          return renderShopVerificationStatusFilter(filterType)
        case FILTER_TYPES.categories:
          return renderCategoryFilter(filterType)

        default:
          return null
      }
    })
  }

  const getNonEmptyFilters = () => {
    return Object.fromEntries(
      // eslint-disable-next-line no-unused-vars
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== undefined)
    )
  }

  const renderPagination = (meta) => {
    return <Pagination paginationMeta={meta} onPageChange={(page) => updateFilters({ page })} />
  }

  return {
    filters: getNonEmptyFilters(),
    updateFilters,
    renderFilters,
    renderPagination,
  }
}
